/**
 * API Service - Domain-Split Architecture
 *
 * This module provides backward-compatible exports while organizing
 * API methods into domain-specific modules.
 *
 * Usage (backward compatible):
 *   import { apiService } from '../services/api';
 *   apiService.getProject(id);
 *
 * Usage (targeted import):
 *   import { projectsApi } from '../services/api';
 *   projectsApi.getProject(id);
 */

// Re-export client utilities
export { apiClient, BaseAPIService, baseApi } from './client';

// Issue #784: runtime class re-exports so consumers (IssueChatThread,
// FindingChatThread) can use `err instanceof IdempotencyInFlightError`
// checks via the barrel. DO NOT change to `export type` — that erases
// the constructor identity at runtime and silently breaks `instanceof`.
export { IdempotencyInFlightError, IdempotencyDifferentBodyError } from './chatApi';

// Import domain APIs
import { authApi } from './authApi';
import { analysisRunsApi } from './analysisRunsApi';
import { projectsApi } from './projectsApi';
import { documentsApi } from './documentsApi';
import { analysisApi } from './analysisApi';
import { pipelineApi, multiAgentApi } from './pipelineApi';
import { sessionApi } from './sessionApi';
import { compareApi } from './compareApi';
import { matterApi } from './matterApi';
import { chatApi } from './chatApi';
import { searchApi } from './searchApi';
import { clauseRevisionApi } from './clauseRevisionApi';
import { negotiationApi } from './negotiationApi';
import { playbookApi } from './playbookApi';
import { playbookMatchApi } from './playbookMatchApi';
import { workbenchApi } from './workbenchApi';
import { adminApi } from './adminApi';
import { feedbackApi } from './feedbackApi';
import { quickActionsApi } from './quickActionsApi';

export type {
  QuickActionStructured,
  QuickActionScope,
  QuickActionFinding,
  QuickActionResponse,
} from './quickActionsApi';

// Re-export types from chatApi (Task 216 + Plan 174)
export type { MatterChatSession, MatterChatSessionsResponse, ScopedChatCounts } from './chatApi';

// Re-export types from searchApi (Task 238)
export type { SearchResult, SearchResponse } from './searchApi';

// Re-export types from clauseRevisionApi (Task 241)
// Re-export types from negotiationApi (Task 302)
export type { NegotiationFinding, NegotiationFindingsResponse, NegotiationDraftResult, NegotiationDraftResponse, NegotiationConfigPayload } from './negotiationApi';

export type {
  ClauseRevision,
  RevisionSourceType,
  RevisionChangeType,
  RevisionStatus,
  RevisionNoteType,
  ChangeManifestEntry,
  AnchorTriplet,
  ClauseSignature,
  GenerateRedraftParams,
  CreateRevisionParams,
  UpdateRevisionParams,
  DocumentRevisionsResponse,
  ChangeManifestResponse,
} from './clauseRevisionApi';

// Export individual domain APIs for targeted imports
export {
  authApi,
  analysisRunsApi,
  projectsApi, // Legacy compatibility
  documentsApi,
  analysisApi,
  pipelineApi,
  multiAgentApi, // Backward compatibility alias
  sessionApi,
  compareApi,
  matterApi,
  chatApi,
  searchApi,
  clauseRevisionApi,
  negotiationApi,
  playbookApi,
  playbookMatchApi,
  workbenchApi,
  adminApi,
  feedbackApi,
  quickActionsApi
};

// Compose apiService from all domain APIs for backward compatibility
export const apiService = {
  // Auth methods
  getProfile: authApi.getProfile.bind(authApi),

  // Project methods
  createProject: projectsApi.createProject.bind(projectsApi),
  createProjectWithWorkflow: projectsApi.createProjectWithWorkflow.bind(projectsApi),
  getProjects: projectsApi.getProjects.bind(projectsApi),
  getProject: projectsApi.getProject.bind(projectsApi),
  updateProject: projectsApi.updateProject.bind(projectsApi),
  deleteProject: projectsApi.deleteProject.bind(projectsApi),

  // Document methods
  uploadDocument: documentsApi.uploadDocument.bind(documentsApi),
  getDocuments: documentsApi.getDocuments.bind(documentsApi),
  getDocument: documentsApi.getDocument.bind(documentsApi),
  deleteDocument: documentsApi.deleteDocument.bind(documentsApi),
  updateDocumentCategory: documentsApi.updateDocumentCategory.bind(documentsApi),
  renameDocument: documentsApi.renameDocument.bind(documentsApi),

  // Analysis methods
  configureWorkflow: analysisApi.configureWorkflow.bind(analysisApi),
  getWorkflowConfig: analysisApi.getWorkflowConfig.bind(analysisApi),
  startAnalysis: analysisApi.startAnalysis.bind(analysisApi),
  getAnalysisStatus: analysisApi.getAnalysisStatus.bind(analysisApi),
  getAnalysisResults: analysisApi.getAnalysisResults.bind(analysisApi),
  addIssueComment: analysisApi.addIssueComment.bind(analysisApi),
  getIssueComment: analysisApi.getIssueComment.bind(analysisApi),
  deleteIssueComment: analysisApi.deleteIssueComment.bind(analysisApi),
  healthCheck: analysisApi.healthCheck.bind(analysisApi),

  // Pipeline methods (with backward compatibility aliases)
  startPipelineAnalysis: pipelineApi.startPipelineAnalysis.bind(pipelineApi),
  startMultiAgentAnalysis: pipelineApi.startPipelineAnalysis.bind(pipelineApi), // Alias
  getStageStatus: pipelineApi.getStageStatus.bind(pipelineApi),
  getAgentStatus: pipelineApi.getStageStatus.bind(pipelineApi), // Alias
  getWorkflowExecution: pipelineApi.getWorkflowExecution.bind(pipelineApi),
  getAnalysisTrace: pipelineApi.getAnalysisTrace.bind(pipelineApi),
  getEnhancedAnalysisStatus: pipelineApi.getEnhancedAnalysisStatus.bind(pipelineApi),
  getWorkflowPerformanceMetrics: pipelineApi.getWorkflowPerformanceMetrics.bind(pipelineApi),
  getWorkflowCostAnalysis: pipelineApi.getWorkflowCostAnalysis.bind(pipelineApi),
  getFallbackStatus: pipelineApi.getFallbackStatus.bind(pipelineApi),

  // Session methods
  createSession: sessionApi.createSession.bind(sessionApi),
  getActiveSession: sessionApi.getActiveSession.bind(sessionApi),
  updateSession: sessionApi.updateSession.bind(sessionApi),
  deactivateSession: sessionApi.deactivateSession.bind(sessionApi),
  saveSnapshot: sessionApi.saveSnapshot.bind(sessionApi),
  autoSave: sessionApi.autoSave.bind(sessionApi),
  getRecoverySessions: sessionApi.getRecoverySessions.bind(sessionApi),
  recoverSession: sessionApi.recoverSession.bind(sessionApi),
  getLatestSnapshot: sessionApi.getLatestSnapshot.bind(sessionApi),
  getSessionHistory: sessionApi.getSessionHistory.bind(sessionApi),
  cleanupSessions: sessionApi.cleanupSessions.bind(sessionApi),

  // Compare methods
  configureCompareWorkflow: compareApi.configureCompareWorkflow.bind(compareApi),
  getCompareConfiguration: compareApi.getCompareConfiguration.bind(compareApi),
  updateCompareConfiguration: compareApi.updateCompareConfiguration.bind(compareApi),
  validateCompareConfiguration: compareApi.validateCompareConfiguration.bind(compareApi),
  getDefaultCompareConfiguration: compareApi.getDefaultCompareConfiguration.bind(compareApi),
  assignDocumentRole: compareApi.assignDocumentRole.bind(compareApi),
  bulkAssignDocumentRoles: compareApi.bulkAssignDocumentRoles.bind(compareApi),
  getProjectDocumentRoles: compareApi.getProjectDocumentRoles.bind(compareApi),
  removeDocumentRole: compareApi.removeDocumentRole.bind(compareApi),
  validateProjectDocumentRoles: compareApi.validateProjectDocumentRoles.bind(compareApi),

  // Matter methods
  getMatters: matterApi.getMatters.bind(matterApi),
  getMatter: matterApi.getMatter.bind(matterApi),
  createMatter: matterApi.createMatter.bind(matterApi),
  updateMatter: matterApi.updateMatter.bind(matterApi),
  deleteMatter: matterApi.deleteMatter.bind(matterApi),
  getMatterDocuments: matterApi.getMatterDocuments.bind(matterApi),
  uploadDocumentToMatter: matterApi.uploadDocumentToMatter.bind(matterApi),
  getMatterWorkflows: matterApi.getMatterWorkflows.bind(matterApi),
  createWorkflowInMatter: matterApi.createWorkflowInMatter.bind(matterApi),

  // Chat methods
  createChatSession: chatApi.createChatSession.bind(chatApi),
  getChatSessions: chatApi.getChatSessions.bind(chatApi),
  getChatSession: chatApi.getChatSession.bind(chatApi),
  updateChatSession: chatApi.updateChatSession.bind(chatApi),
  deleteChatSession: chatApi.deleteChatSession.bind(chatApi),
  sendChatMessage: chatApi.sendChatMessage.bind(chatApi),
  sendChatMessageStream: chatApi.sendChatMessageStream.bind(chatApi),
  getChatMessages: chatApi.getChatMessages.bind(chatApi),
  getMessageCitations: chatApi.getMessageCitations.bind(chatApi),
  improveChatPrompt: chatApi.improveChatPrompt.bind(chatApi),
  // Task 216: Matter-scoped chat methods
  getMatterChatSessions: chatApi.getMatterChatSessions.bind(chatApi),
  createMatterChatSession: chatApi.createMatterChatSession.bind(chatApi),
  // Plan 143: scoped chat session lookup
  getChatSessionByScope: chatApi.getChatSessionByScope.bind(chatApi),
  // Plan 174 (#763): per-source scoped chat user-turn counts
  getScopedChatMessageCounts: chatApi.getScopedChatMessageCounts.bind(chatApi),

  // Generic HTTP methods (from baseApi via authApi which extends BaseAPIService)
  get: authApi.get.bind(authApi),
  post: authApi.post.bind(authApi),
  put: authApi.put.bind(authApi),
  patch: authApi.patch.bind(authApi),
  delete: authApi.delete.bind(authApi),
};

export default apiService;
